import React, { useEffect } from 'react';
import { useCalculateTaxesMutation, useCalculateShippingMutation, useCompleteCheckoutMutation, useGetOrderQuery, useGetStorefrontQuery } from '../Api/Api';
import useStyles from '../styles';
import {
    Checkbox,
    Button,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    MessageBarActions,
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogTrigger,
    DialogContent,
    DialogActions
} from '@fluentui/react-components';
import { DismissRegular, CheckmarkCircleFilled, WarningRegular } from "@fluentui/react-icons";
import { IAddress } from '../Api/Models/Order';
import AddressForm from '../Components/Order/AddressForm';
import { useNavigate, useParams } from 'react-router-dom';
import MarkdownView from "react-showdown";
import FullPageSpinner from "../Components/Spinner/FullPageSpinner";
import LocationSelection from '../Components/Order/LocationSelection';
import { ILocation } from '../Api/Models/Customer';
import OrderTable from '../Components/Order/OrderTable';
import { CheckAddress, ConvertToAddress, LocationToAddress } from '../Components/Helpers/AddressHelpers';
import { ITaxRequest } from '../Api/Models/Cart';
import CAN, {ORDERS} from "../Components/Auth/Permissions";
import { parseCheckoutPageConfig } from '../Components/Helpers/checkoutPageConfig';
import { elementStyleFrom } from '../Components/Helpers/searchPageConfig';
import { useLocalization } from '../Components/Helpers/useLocalization';

const OrderPage: React.FC = () => {
    const { orderId } = useParams<{ orderId: string }>();
    const order = useGetOrderQuery({ orderId: orderId ?? '' }, { skip: !orderId });
    const L = useLocalization();

    const sfSettings = useGetStorefrontQuery();
    // Merchant checkout-page styling (checkoutPageConfig JSON). Each section applies to a bounding
    // component; theme-forced elements (the total row) get an !important ref, plain boxes/headings/buttons inline.
    const checkoutConfig = React.useMemo(() => parseCheckoutPageConfig(sfSettings.data?.checkoutPageConfig), [sfSettings.data?.checkoutPageConfig]);
    const containerStyle = React.useMemo(() => elementStyleFrom(checkoutConfig.container), [checkoutConfig.container]);
    const titleStyle = React.useMemo(() => elementStyleFrom(checkoutConfig.title, 'heading'), [checkoutConfig.title]);
    const summaryBoxStyle = React.useMemo(() => elementStyleFrom(checkoutConfig.summaryBox), [checkoutConfig.summaryBox]);
    const formContainerStyle = React.useMemo(() => elementStyleFrom(checkoutConfig.formContainer), [checkoutConfig.formContainer]);
    const sectionHeadingStyle = React.useMemo(() => elementStyleFrom(checkoutConfig.sectionHeading, 'heading'), [checkoutConfig.sectionHeading]);
    const placeOrderButtonStyle = React.useMemo(() => elementStyleFrom(checkoutConfig.placeOrderButton), [checkoutConfig.placeOrderButton]);
    const [shippingAddress, setShippingAddress] = React.useState<IAddress>({});
    const [shippingLocation, setShippingLocation] = React.useState<string | undefined>();
    const [shippingLocationDetails, setShippingLocationDetails] = React.useState<ILocation | undefined>();
    const [billingAddress, setBillingAddress] = React.useState<IAddress>({});
    const [billingLocation, setBillingLocation] = React.useState<string | undefined>();
    const [billingLocationDetails, setBillingLocationDetails] = React.useState<ILocation | undefined>();
    const [copyFromShipping, setCopyFromShipping] = React.useState<boolean>(true);
    const styles = useStyles();
    const [completeOrder] = useCompleteCheckoutMutation();
    const [ordering, setOrdering] = React.useState(false);
    const navigate = useNavigate();
    const [error, setError] = React.useState<string | undefined>();
    const [warn, setWarn] = React.useState<string | undefined>();
    const [termsAgreed, setTermsAgreed] = React.useState<boolean>(false);
    const [termsOpen, setTermsOpen] = React.useState<boolean>(false);
    // The end user must scroll through the entire T&C before they can accept.
    const [termsScrolledToBottom, setTermsScrolledToBottom] = React.useState<boolean>(false);
    const termsScrollRef = React.useRef<HTMLDivElement>(null);

    const handleTermsScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 8) {
            setTermsScrolledToBottom(true);
        }
    };

    // If the terms are short enough that they don't scroll, allow acceptance immediately.
    React.useEffect(() => {
        if (termsOpen) {
            const el = termsScrollRef.current;
            if (el && el.scrollHeight - el.clientHeight < 8) {
                setTermsScrolledToBottom(true);
            }
        }
    }, [termsOpen]);
    const [taxes, setTaxes] = React.useState<number | undefined>();
    const [shipping, setShipping] = React.useState<number | undefined>();
    const [addressValidForShipping, setAddressValidForShipping] = React.useState<boolean>(false);
    const [calculating, setCalculating] = React.useState(false);
    const [calculatingShippingCharges, setCalculatingShippingCharges] = React.useState(false);
    const [calculateTaxes] = useCalculateTaxesMutation();
    const [calculateShipping] = useCalculateShippingMutation();

    const [termsAndConditions, setTermsAndConditions] = React.useState<string | undefined>();
    useEffect(() => {
        setTermsAndConditions(sfSettings.data?.termsAndConditions)
    }, [sfSettings.data?.termsAndConditions])

    // When the storefront has no terms to accept, don't block order placement on agreement.
    useEffect(() => {
        if (sfSettings.data && !sfSettings.data.termsAndConditions) {
            setTermsAgreed(true);
        }
    }, [sfSettings.data])

    const canPlaceOrder = CAN(ORDERS);
    if (!canPlaceOrder) {
        navigate('/')
    }

    useEffect(() => {
        if(order.error){
            navigate(`/checkoutPrerequisites/${orderId}`)
        }
    }, [order.error]);

    useEffect(() => {
        if (shippingLocationDetails?.address && shippingLocationDetails?.city && shippingLocationDetails?.state && shippingLocationDetails?.postalCode) {

            populateShippingChargesPlusTaxes()
        }
    }, [shippingLocationDetails]);

    const checkAddresses = () => {
        const error = CheckAddress(L('localization_address_shipping_label'), shippingAddress, shippingLocationDetails, L);
        if (error) {
            setError(error);
            return false;
        }
        if (!copyFromShipping) {
            const error = CheckAddress(L('localization_address_billing_label'), billingAddress, billingLocationDetails, L);
            if (error) {
                setError(error);
                return false;
            }
        }
        return true;
    }
    const placeOrder = async () => {
        setOrdering(true);
        const shipping = ConvertToAddress(shippingAddress, shippingLocationDetails);
        const billing = copyFromShipping ? shipping : ConvertToAddress(billingAddress, billingLocationDetails);
        if (checkAddresses() && orderId) {
            await completeOrder({
                orderId,
                request: {
                    shippingAddress: shipping,
                    billingAddress: billing,
                    orderId: orderId,
                }
            }).unwrap().then((e) => {
                if (e.success) {
                    navigate('/order-complete')
                } else {
                    setError(e.error ?? L('localization_order_place_error'))
                }
            });
        }
        setOrdering(false);
    }

    // Debounced version to avoid too many API calls
    const debounceRef = React.useRef<NodeJS.Timeout | null>(null);
    const populateShippingChargesPlusTaxes = React.useCallback(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(async () => {
            if (!orderId) return;
            setCalculatingShippingCharges(true);
            setCalculating(true);

            const shipToAddress = ConvertToAddress(shippingAddress, shippingLocationDetails);

            const shippingPromise = calculateShipping({
                orderId: orderId,
                request: { shipTo: shipToAddress }
            });

            let taxPromise: Promise<any> = Promise.resolve({ data: undefined });
            if (sfSettings.data?.taxEnabled) {
                const taxRequest: ITaxRequest = { address: shipToAddress };
                taxPromise = calculateTaxes({
                    orderId: orderId,
                    request: taxRequest
                });
            }

            const [shippingResponse, taxResponse] = await Promise.all([shippingPromise, taxPromise]);

            setShipping(shippingResponse.data ? shippingResponse.data.amount : undefined);
            setTaxes(taxResponse.data ? taxResponse.data.tax : undefined);

            setCalculatingShippingCharges(false);
            setCalculating(false);
        }, 1000);
    }, [orderId, shippingAddress, shippingLocationDetails, calculateShipping, sfSettings.data?.taxEnabled, calculateTaxes]);

    const populateShipping = (location?: ILocation) => {
        if (location?.type === 'PreviousOrderAddress') {
            // Pre-populate address fields from the previous order address,
            // but keep fields editable (don't lock via locationDetails)
            const converted = LocationToAddress(location);
            setShippingLocationDetails(undefined);
            setShippingLocation(location.index.toString());
            updateShippingAddress(converted);
        } else {
            setShippingLocationDetails(location);
            setShippingLocation(location !== undefined ? location.index.toString() : undefined);
            if (location) {
                setShippingAddress(prev => ({ name: prev.name, phoneNumber: prev.phoneNumber }));
            }
        }
    }

    const populateBilling = (location?: ILocation) => {
        if (location?.type === 'PreviousOrderAddress') {
            // Pre-populate billing address fields, keep them editable
            const converted = LocationToAddress(location);
            setBillingLocationDetails(undefined);
            setBillingLocation(location.index.toString());
            setBillingAddress(converted);
        } else {
            setBillingLocationDetails(location);
            setBillingLocation(location !== undefined ? location.index.toString() : undefined);
            if (location) {
                setBillingAddress(prev => ({ name: prev.name, phoneNumber: prev.phoneNumber }));
            }
        }
    }
    

    if (order.isLoading || sfSettings.isLoading) {
        return <FullPageSpinner message={L('localization_common_loading')} />
    }

    if (ordering) {
        return <FullPageSpinner message={L('localization_order_submitting')} />
    }
    
    const updateShippingAddress = (address: IAddress) => {
        if(!!(address.address1 && address.city && address.state && address.zipCode)) {
            if(!addressValidForShipping) {
                populateShippingChargesPlusTaxes()
            }
            else if(address.address1 !== shippingAddress.address1 ||
                address.city !== shippingAddress.city ||
                address.state !== shippingAddress.state ||
                address.zipCode !== shippingAddress.zipCode) {
                populateShippingChargesPlusTaxes();
            }
            setAddressValidForShipping(true);
        }
        else{
            setAddressValidForShipping(false);
        }
        setShippingAddress(address);
    }

    return (
        <div className={styles.mainContent} style={containerStyle}>
            <h1 style={titleStyle}>{L('localization_order_summary_title')}</h1>
            <div className='order-details'>
                <div className='order-details-table-col'>
                    <div className='order-details-table-box' style={summaryBoxStyle}>
                        <div className='order-details-table'>
                            {order.data && <OrderTable
                                calculatingShipping={calculatingShippingCharges}
                                shippingAmount={shipping}
                                order={order.data}
                                taxAmount={taxes}
                                calculatingTax={calculating}
                            />}
                        </div>
                    </div>
                </div>
                <div className='order-details-form' style={formContainerStyle}>
                    <div>
                        <h2 style={sectionHeadingStyle}>{L('localization_order_shipping_information')}</h2>
                        {sfSettings.data?.locationLookupEnabled && <LocationSelection
                            selectedLocation={shippingLocation}
                            onChange={populateShipping}
                            label={L('localization_order_shipping_location')}
                        />}
                        <AddressForm
                            address={shippingAddress}
                            onChange={updateShippingAddress}
                            pullingFromLocation={shippingLocationDetails !== undefined}
                            locationDetails={shippingLocationDetails}
                        />
                        <h2 style={sectionHeadingStyle}>{L('localization_order_billing_information')}</h2>
                        <Checkbox
                            label={L('localization_order_copy_from_shipping')}
                            onChange={(e) => setCopyFromShipping(e.target.checked)}
                            checked={copyFromShipping}
                        />
                        <div style={{display: copyFromShipping ? 'none' : 'block'}}>
                            {sfSettings.data?.locationLookupEnabled && <LocationSelection
                                selectedLocation={billingLocation}
                                onChange={populateBilling}
                                label={L('localization_order_billing_location')}
                            />}
                            <AddressForm
                                address={billingAddress}
                                onChange={setBillingAddress}
                                pullingFromLocation={billingLocationDetails !== undefined}
                                locationDetails={billingLocationDetails}
                            />
                        </div>
                        <hr />
                        {termsAndConditions &&
                            <div style={{ marginTop: '2em' }} className='terms-and-conditions'>
                                <Button appearance='subtle' className='terms-view-btn' onClick={() => setTermsOpen(true)}>
                                    {L('localization_order_open_accept_terms')}
                                </Button>
                                <div style={{ marginTop: '0.75em', display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                                    {termsAgreed
                                        ? <>
                                            <CheckmarkCircleFilled style={{ color: '#107c10', fontSize: '20px', flexShrink: 0 }} />
                                            <span>{L('localization_order_terms_accepted')}</span>
                                          </>
                                        : <>
                                            <WarningRegular style={{ fontSize: '20px', flexShrink: 0 }} />
                                            <span>{L('localization_order_terms_required')}</span>
                                          </>
                                    }
                                </div>
                                <Dialog open={termsOpen} onOpenChange={(_, d) => setTermsOpen(d.open)}>
                                    <DialogSurface style={{ maxWidth: '600px', width: '90vw' }}>
                                        <DialogBody>
                                            <DialogTitle
                                                action={
                                                    <DialogTrigger action='close'>
                                                        <Button appearance='subtle' icon={<DismissRegular />} aria-label='close' />
                                                    </DialogTrigger>
                                                }
                                            >
                                                {L('localization_order_terms_title')}
                                            </DialogTitle>
                                            <DialogContent>
                                                <div
                                                    ref={termsScrollRef}
                                                    onScroll={handleTermsScroll}
                                                    style={{ maxHeight: '50vh', overflowY: 'auto' }}
                                                >
                                                    <MarkdownView
                                                        markdown={termsAndConditions ?? ''}
                                                        options={{ tables: true, emoji: true }}
                                                    />
                                                </div>
                                            </DialogContent>
                                            <DialogActions>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5em', width: '100%' }}>
                                                    {!termsScrolledToBottom &&
                                                        <span style={{ fontSize: '0.9em', opacity: 0.8 }}>
                                                            {L('localization_order_terms_scroll_prompt')}
                                                        </span>
                                                    }
                                                    <Checkbox
                                                        required
                                                        checked={termsAgreed}
                                                        disabled={!termsScrolledToBottom}
                                                        label={L('localization_order_terms_agree_checkbox')}
                                                        onChange={(_, data) => setTermsAgreed(data.checked === true)}
                                                    />
                                                </div>
                                            </DialogActions>
                                        </DialogBody>
                                    </DialogSurface>
                                </Dialog>
                            </div>
                        }
                        <hr />

                        {error && <div style={{ marginTop: '1em' }}>
                            <MessageBar
                                intent={'error'}
                            >
                                <MessageBarBody className='error-message'>
                                    <MessageBarTitle>{L('localization_order_error_title')}</MessageBarTitle>
                                    {error}
                                </MessageBarBody>
                                <MessageBarActions
                                    containerAction={
                                        <Button
                                            aria-label="dismiss"
                                            appearance="transparent"
                                            icon={<DismissRegular />}
                                            onClick={() => { setError(undefined) }}
                                        />
                                    }
                                >
                                </MessageBarActions>
                            </MessageBar>
                        </div>
                        }
                        {warn && <div style={{ marginTop: '1em' }}>
                            <MessageBar
                                intent={'warning'}
                            >
                                <MessageBarBody className='error-message'>
                                    <MessageBarTitle>{L('localization_order_warning_title')}</MessageBarTitle>
                                    {warn}
                                </MessageBarBody>
                                <MessageBarActions
                                    containerAction={
                                        <Button
                                            aria-label="dismiss"
                                            appearance="transparent"
                                            icon={<DismissRegular />}
                                            onClick={() => { setWarn(undefined) }}
                                        />
                                    }
                                >
                                </MessageBarActions>
                            </MessageBar>
                        </div>
                        }
                        <div style={{ marginTop: '1em' }}>
                            <Button onClick={placeOrder} disabled={ordering || !termsAgreed || calculating} appearance='primary' style={placeOrderButtonStyle}>{L('localization_order_place_order')}</Button>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    )
}

export default OrderPage;